import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { TableRequestV3, TableResponse } from 'services/types'
import { sendNotification } from 'components'
import type { BaseColorType } from '@evrekadev/evreka-ui-components'

type GetImportDetailsRequest = {
  importId: string
}

// TODO: refer to this thread for exact type: https://evrekagroup.slack.com/archives/C0210A23VSS/p1669102195732689
type ImporDetailData = {
  id: string
  module: string
  page: string
  create_date: string
  file: {
    name: string
    url: string
  } | null
  status: {
    id: number
    label: string
    description: string | null
    color: string
  }
  result?: {
    success?: Record<string, Record<string, string>>
    error_rows?: Record<string, Record<string, string>>
  }
}

type GetImportDetailsResponse = {
  success: boolean
  detail: {
    data: ImporDetailData
  }
}

export const ExportItemStatus = {
  NEW: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const

export const ExportStatus = {
  ...ExportItemStatus,
  PARTIAL_COMPLETED: 4,
} as const

type ExportDetailItem = {
  id: string
  created_at: string // ISO date string
  start_point: number
  end_point: number
  part: number
  retry_count: number
  result: string | null
  status: (typeof ExportItemStatus)[keyof typeof ExportItemStatus]
  file: string | null
}

type ExportDetailResponse = {
  id: string
  module: string
  page: string
  status: {
    id: (typeof ExportStatus)[keyof typeof ExportStatus]
    label: string
    color: BaseColorType
  }
  created_at: string // "21.03.2025 18:01" format
  requested_by: string
  file: string | null
  items: Array<ExportDetailItem>
}

type ExportDetailRequest = {
  clientId: number
  exportId: string
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  tagTypes: ['Reports', 'ExportReports', 'ImportReports', 'ImportDetail', 'ExportDetail'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/int-api'}`,
  }),
  endpoints: (builder) => ({
    getExportedReports: builder.query<TableResponse<{}>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `/export/${clientId}/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['ExportReports'],
    }),
    getImportRequests: builder.query<TableResponse<{}>, TableRequestV3>({
      query: ({ clientId, ...data }) => ({
        url: `/import/${clientId}/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['ImportReports'],
    }),
    getImportDetails: builder.query<GetImportDetailsResponse, GetImportDetailsRequest>({
      query: ({ importId }) => ({
        url: `/import/${importId}/detail/`,
        method: 'GET',
      }),
      providesTags: ['ImportDetail'],
      async onQueryStarted() {
        try {
          // `onSuccess` side-effect
        } catch (err) {
          sendNotification({
            type: 'error',
            toastContent:
              'An error occurred while trying to fetch import details. Please try again later.',
          })
          // `onError` side-effect
        }
      },
    }),
    getExportDetails: builder.query<ExportDetailResponse, ExportDetailRequest>({
      query: ({ clientId, exportId }) => ({
        url: `/export/${clientId}/detail/${exportId}/`,
        method: 'GET',
        errorOverride: true,
      }),
      providesTags: ['ExportDetail'],
    }),
  }),
})
